import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Badge, BadgeColor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Typography from "@/components/ui/typography";
import { Mentor, MentorsKeys } from "@/features/mentors/api/mentors";
import {
  refreshOnboardingLink,
  startPayoutOnboarding,
} from "@/features/payouts/api/mentor-payouts";
import { handleError } from "@/utils/error-handling";

type PayoutStatus = "not_started" | "pending" | "completed";

const getPayoutStatus = (mentor: Mentor | undefined): PayoutStatus => {
  if (!mentor) return "not_started";

  if (mentor.payoutsActivated) {
    return "completed";
  }

  if (mentor.stripeRecipientAccountId) {
    return "pending";
  }

  return "not_started";
};

const statusConfig: Record<PayoutStatus, { label: string; color: BadgeColor }> =
  {
    not_started: { label: "Not Started", color: "red" },
    pending: { label: "Pending", color: "yellow" },
    completed: { label: "Completed", color: "green" },
  };

interface PayoutsSetupCardProps {
  mentor: Mentor | undefined;
}

const PayoutsSetupCard = ({ mentor }: PayoutsSetupCardProps) => {
  const queryClient = useQueryClient();
  const status = getPayoutStatus(mentor);
  const { label, color } = statusConfig[status];

  const { mutate: startOnboarding, isPending: isStarting } = useMutation({
    mutationFn: startPayoutOnboarding,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [MentorsKeys.GET_ME] });
      window.open(data.onboardingUrl, "_blank");
    },
    onError: handleError,
  });

  const { mutate: refreshLink, isPending: isRefreshing } = useMutation({
    mutationFn: refreshOnboardingLink,
    onSuccess: (data) => {
      window.open(data.onboardingUrl, "_blank");
    },
    onError: handleError,
  });

  const handleSetupPayouts = () => {
    if (status === "pending") {
      refreshLink();
    } else {
      startOnboarding();
    }
  };

  const isPending = isStarting || isRefreshing;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <Typography size="h3">Payouts</Typography>
            </CardTitle>
            <CardDescription>
              <Typography size="b3">
                Connect your bank account to receive your earnings
              </Typography>
            </CardDescription>
          </div>
          <Badge color={color} variant="opaque">
            {label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Typography size="b3" className="text-muted-foreground">
          Connect your bank account through Stripe to receive your mentor
          earnings. The setup takes just a few minutes and your banking
          information is secured by Stripe.
        </Typography>
        {status !== "completed" && (
          <Button
            type="button"
            onClick={handleSetupPayouts}
            isLoading={isPending}
          >
            {status === "pending" ? "Continue Setup" : "Set Up Payouts"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PayoutsSetupCard;
